import { DomainElement } from '../../../library/Core/SmartTypes';
import { BreakupCompany, BreakupRow } from './types';

export const ALL_INSTITUTES_VALUE = '__ALL__';

export type BreakupFilterKind = 'industry' | 'functionalArea';

export function companyDisplayName(company: BreakupCompany): string {
    if (typeof company === 'string') return company;
    return company?.name || '';
}

export function companyIdOf(company: BreakupCompany): string | number | null {
    if (typeof company === 'string') return null;
    return company?.id ?? null;
}

export function findDomainCodeByLabel(
    domain: Map<string, DomainElement[]> | undefined,
    categoryCode: string,
    label: string | null | undefined
): string | null {
    const name = (label || '').trim();
    if (!name || name.toLowerCase() === 'others' || !domain) return null;
    const items = domain.get(categoryCode) || [];
    const needle = name.toLowerCase();
    const hit = items.find((el) => String(el.value || '').trim().toLowerCase() === needle);
    return hit?.code != null && String(hit.code) !== '' ? String(hit.code) : null;
}

export function resolveRowCode(
    row: Pick<BreakupRow, 'name' | 'code'>,
    domain: Map<string, DomainElement[]> | undefined,
    kind: BreakupFilterKind
): string | null {
    if (row.code) return String(row.code);
    const category = kind === 'industry' ? 'INDUSTRY_CODE' : 'FUNCTIONAL_AREA_CODE';
    return findDomainCodeByLabel(domain, category, row.name);
}

export function resolveCompanyId(
    company: BreakupCompany,
    domain: Map<string, DomainElement[]> | undefined
): string | null {
    const id = companyIdOf(company);
    if (id != null && String(id) !== '') return String(id);
    return findDomainCodeByLabel(domain, 'COMPANY_CODE', companyDisplayName(company));
}

export function campusQueryValue(instituteId?: string | number | null): string | null {
    if (instituteId == null || String(instituteId) === '' || String(instituteId) === ALL_INSTITUTES_VALUE) {
        return null;
    }
    return String(instituteId);
}

/** Chart label → JOB_SENIORITY_LEVEL code (matches directory Designation Level). */
const SENIORITY_LABEL_TO_CODE: Record<string, string> = {
    managerothers: 'EXECUTIVE',
    midmanagement: 'MID_MANAGEMENT',
    seniormanagement: 'MANAGEMENT',
    executiveleadership: 'EXECUTIVE_LEADERSHIP',
    founders: 'LEADERSHIP',
    foundersverified: 'LEADERSHIP',
    foundersentrepreneurs: 'LEADERSHIP',
};

/** Same set as snapshot `alumni_with_professional_details` (Alumni Seniority Classification). */
export const PROFESSIONAL_DETAILS_SENIORITY_CODES = [
    'EXECUTIVE',
    'MID_MANAGEMENT',
    'MANAGEMENT',
    'EXECUTIVE_LEADERSHIP',
    'LEADERSHIP',
];

export function resolveSeniorityLevelCode(label: string | null | undefined): string | null {
    const key = (label || '').toLowerCase().replace(/[^a-z]/g, '');
    if (!key) return null;
    if (key.startsWith('founder')) return 'LEADERSHIP';
    return SENIORITY_LABEL_TO_CODE[key] ?? null;
}

export const PROFESSIONAL_DETAILS_PARAM = 'professionalDetails';

export function isProfessionalDetailsQuery(searchParams: URLSearchParams): boolean {
    const flag = searchParams.get(PROFESSIONAL_DETAILS_PARAM);
    if (flag == null || flag === '' || flag === '0' || flag.toLowerCase() === 'false') return false;
    return true;
}

export function seniorityCodesFromSearchParams(searchParams: URLSearchParams): string[] {
    if (isProfessionalDetailsQuery(searchParams)) return [...PROFESSIONAL_DETAILS_SENIORITY_CODES];
    return searchParams
        .getAll('seniorityLevelCode')
        .flatMap((value) => String(value).split(','))
        .map((code) => code.trim())
        .filter(Boolean);
}

export function buildAlumniDirectoryPath(params: {
    industryCode?: string | null;
    functionalAreaCode?: string | null;
    seniorityLevelCode?: string | string[] | null;
    professionalDetails?: boolean;
    jobCompanyId?: string | number | null;
    instituteId?: string | number | null;
}): string {
    const q = new URLSearchParams();
    if (params.industryCode) q.set('industryCode', String(params.industryCode));
    if (params.functionalAreaCode) q.set('functionalAreaCode', String(params.functionalAreaCode));
    if (params.professionalDetails) {
        q.set(PROFESSIONAL_DETAILS_PARAM, 'true');
    } else {
        const seniorityCodes = Array.isArray(params.seniorityLevelCode)
            ? params.seniorityLevelCode
            : params.seniorityLevelCode
              ? [params.seniorityLevelCode]
              : [];
        seniorityCodes.filter(Boolean).forEach((code) => q.append('seniorityLevelCode', String(code)));
    }
    if (params.jobCompanyId != null && String(params.jobCompanyId) !== '') {
        q.set('jobCompanyId', String(params.jobCompanyId));
    }
    const campus = campusQueryValue(params.instituteId);
    if (campus) q.set('instituteId', campus);
    const qs = q.toString();
    return qs ? `/alumni/directory?${qs}` : '/alumni/directory';
}

export type EntrepreneurDirectoryLinkParams = {
    selectedCard?: string | null;
    instituteId?: string | number | null;
    revenueBand?: string | null;
    isFirstGeneration?: boolean;
    isFamilyManaged?: boolean;
    topRevenueEntrepreneurs?: boolean;
};

export function buildEntrepreneurDirectoryPath(params: EntrepreneurDirectoryLinkParams = {}): string {
    const q = new URLSearchParams();
    if (params.selectedCard) q.set('selectedCard', String(params.selectedCard));
    const campus = campusQueryValue(params.instituteId);
    if (campus) q.set('instituteId', campus);
    if (params.revenueBand) q.set('revenueBand', String(params.revenueBand));
    if (params.isFirstGeneration) q.set('isFirstGeneration', 'true');
    if (params.isFamilyManaged) q.set('isFamilyManaged', 'true');
    if (params.topRevenueEntrepreneurs) q.set('topRevenueEntrepreneurs', 'true');
    const qs = q.toString();
    return qs ? `/entrepreneur/directory?${qs}` : '/entrepreneur/directory';
}

export function analysisSchoolIds(
    instituteId: string | number,
    selectedInstituteId?: string,
    instituteOptions?: { value: string }[]
): string[] {
    const selected = selectedInstituteId ?? String(instituteId);
    if (selected && selected !== ALL_INSTITUTES_VALUE) return [String(selected)];
    return (instituteOptions || [])
        .map((o) => o.value)
        .filter((v) => v && v !== ALL_INSTITUTES_VALUE);
}
